import axios, { AxiosResponse } from 'axios';
import { User } from '../types';

// API Base URL - 통합 설정 사용
import { API_BASE_URL } from '../config/api';

// DDD 토큰 저장소 (TokenService 호환성)
import { AuthToken } from '../domains/auth/value-objects/AuthToken';
import { AuthLocalStorageAdapter } from '../domains/auth/repositories/AuthLocalStorageAdapter';

// 기본 오너 권한 정의
const DEFAULT_OWNER_PERMISSIONS = [
  'read:members',
  'write:members',
  'read:seats',
  'write:seats',
  'read:settings',
  'write:settings',
  'read:statistics',
  'write:statistics'
];

// 인증 관련 타입 정의
export interface LoginCredentials {
  email?: string;  // 이메일 로그인
  username?: string; // 호환성을 위한 username (email로 변환됨)
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  session_id?: string;
  user: User;
}

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token?: string;  // 새 리프레시 토큰 (있는 경우)
  token_type: string;
}

export interface UserXCircleResponse {
  id: string;
  username: string;
  name: string;
  email: string;
  role: string;
  org_id: string;
  is_active: boolean;
  last_login: string;
}

// 모든 환경에서 nginx 프록시 사용하되, 다른 API 경로로 시도
const getAuthBaseURL = () => {
  // nginx 프록시 사용 시 (API_BASE_URL이 빈 문자열) /api 경로 추가
  return API_BASE_URL === '' ? '/api' : API_BASE_URL;
};

// Axios 인스턴스 생성 (인증 API용)
const authAxios = axios.create({
  baseURL: getAuthBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: 토큰 자동 첨부
authAxios.interceptors.request.use(
  (config) => {
    // 🔧 auth_access_token 우선, accessToken은 호환성 유지
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 요청 로그 - 실제 전체 URL 표시
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;

    return config;
  },
  (error) => {
    console.error('🚨 [AUTH API] 요청 인터셉터 오류:', error);
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 토큰 만료 처리
authAxios.interceptors.response.use(
  (response) => {
    // 성공 응답 로그
    console.log(
      `✅ [AUTH API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`
    );
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error(
      `❌ [AUTH API] ${originalRequest.method?.toUpperCase()} ${originalRequest.url} - ${error.response?.status}`,
      error.response?.data
    );

    // 401 오류이고 재시도하지 않은 요청인 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        try {

          // 리프레시 토큰으로 새 액세스 토큰 요청
          const refreshResponse = await authAxios.post(
            '/v1/auth/refresh',
            { refresh_token: refreshToken },  // 백엔드 필드명에 맞춤
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
              },
            }
          );

          const newAccessToken = refreshResponse.data.access_token;
          const newRefreshToken = refreshResponse.data.refresh_token || refreshToken;

          // Legacy 키에 저장
          localStorage.setItem('accessToken', newAccessToken);
          if (refreshResponse.data.refresh_token) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // DDD 키에도 저장 (TokenService 호환성)
          try {
            const authToken = AuthToken.fromJWT(newAccessToken, newRefreshToken);
            AuthLocalStorageAdapter.saveToken(authToken);
            AuthLocalStorageAdapter.updateLastActivity();
          } catch (e) {
            console.warn('⚠️ DDD 토큰 저장 실패:', e);
          }

          // 원래 요청에 새 토큰 적용하여 재시도
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          return authAxios(originalRequest);
        } catch (refreshError) {
          console.error('❌ 토큰 갱신 실패:', refreshError);

          // [2026-01-05] 토큰 갱신 실패 시에도 강제 로그아웃 안 함
          // 토큰을 보존하고 요청만 실패 처리 - 사용자가 다시 로그인할 때까지 대기
          console.warn('⚠️ [authAxios] 토큰 갱신 실패 - 토큰 보존, 요청만 실패 처리');
          throw refreshError;
        }
      }
    }

    return Promise.reject(error);
  }
);

// 오너용 Axios 인스턴스
const ownerAxios = axios.create({
  baseURL: `/api/v1/owner`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 인증 API 서비스
// 전화번호 로그인용 타입
export interface PhoneLoginRequest {
  phone: string;
  password: string;
}

export const authApi = {
  // 일반 사용자 로그인
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // 백엔드 자체 JWT 인증: username 필드 사용 (이메일 또는 전화번호)
      const loginData = {
        username: credentials.email || credentials.username,
        password: credentials.password
      };

      console.log('🔍 AUTH API 상세 디버깅:', {
        API_BASE_URL,
        windowLocation: typeof window !== 'undefined' ? {
          href: window.location.href,
          hostname: window.location.hostname,
          port: window.location.port,
          protocol: window.location.protocol,
          pathname: window.location.pathname
        } : 'SERVER',
        authAxiosBaseURL: authAxios.defaults.baseURL,
        actualURL: authAxios.defaults.baseURL + '/v1/auth/login'
      });

      const response: AxiosResponse<{
        access_token?: string;
        refresh_token?: string;
        token_type?: string;
        session_id?: string;
        user?: any;
        detail?: string;
      }> = await authAxios.post('/v1/auth/login', loginData);

      // 백엔드 응답 처리 - access_token이 있으면 성공
      if (!response.data.access_token) {
        throw new Error(response.data.detail || 'Login failed');
      }

      const { access_token, refresh_token, token_type, session_id, user: userData } =
        response.data;

      // 🔍 디버깅: 백엔드 응답 전체 로깅

      // 백엔드에서 user 정보를 바로 반환하는 경우
      let user: User;
      
      if (userData) {
        // 백엔드가 user 정보를 포함한 경우
        user = {
          id: String(userData.id),
          name: userData.name || userData.username,
          user_number: userData.email,
          phone: userData.phone || '',
          email: userData.email,
          is_active: userData.is_active !== undefined ? userData.is_active : true,
          created_at: userData.created_at || new Date().toISOString(),
          updated_at: userData.updated_at || new Date().toISOString(),
          ...(userData.role && { role: userData.role }),
          ...(userData.organization_id && { organization_id: userData.organization_id }),
          ...(userData.organization_id && { org_id: userData.organization_id }),
          ...(userData.DEFAULT_OWNER_PERMISSIONS && { DEFAULT_OWNER_PERMISSIONS: userData.DEFAULT_OWNER_PERMISSIONS }),
          // 첫 로그인 관련 정보 추가
          ...(userData.first_login !== undefined && { first_login: userData.first_login }),
          ...(userData.temp_password !== undefined && { temp_password: userData.temp_password }),
        };
      } else {
        // 사용자 정보 가져오기 (토큰을 헤더에 직접 설정)
        const userResponse: AxiosResponse<UserXCircleResponse> = await authAxios.get(
          '/v1/auth/me',
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          }
        );

        // User 타입으로 변환
        user = {
          id: userResponse.data.id,
          name: userResponse.data.name,
          user_number: userResponse.data.username, // username을 user_number로 매핑
          phone: '', // API 응답에 없는 경우 빈 값
          email: userResponse.data.email,
          is_active: userResponse.data.is_active,
          created_at: new Date().toISOString(), // 임시값
          updated_at: new Date().toISOString(), // 임시값
          // 추가 필드들 (타입 안전성을 위해 조건부 할당)
          ...(userResponse.data.role && { role: userResponse.data.role }),
          ...(userResponse.data.org_id && { org_id: userResponse.data.org_id }),
        };
      }

      // refresh_token이 없으면 명확한 오류 발생
      if (!refresh_token) {
        console.error('❌ 백엔드에서 refresh_token을 제공하지 않았습니다!');
        throw new Error('로그인 응답에 refresh_token이 없습니다. 관리자에게 문의하세요.');
      }

      return {
        user,
        access_token,
        refresh_token,
        token_type: token_type || 'bearer',
        session_id,
      };
    } catch (error: any) {
      console.error('로그인 API 오류:', error);
      console.error('오류 상세:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method
        }
      });
      throw error;
    }
  },

  // 오너 로그인
  async ownerLogin(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // 오너도 같은 자체 JWT 인증 엔드포인트 사용
      const loginData = {
        username: credentials.email || credentials.username,
        password: credentials.password
      };

      // 🔧 자체 JWT Auth 사용
      const authEndpoint = '/v1/auth/login';

      console.log('🔍 오너 AUTH API 상세 디버깅:', {
        API_BASE_URL,
        authEndpoint,
        windowLocation: typeof window !== 'undefined' ? {
          href: window.location.href,
          hostname: window.location.hostname,
          port: window.location.port,
          protocol: window.location.protocol,
          pathname: window.location.pathname
        } : 'SERVER',
        authAxiosBaseURL: authAxios.defaults.baseURL,
        actualURL: authAxios.defaults.baseURL + authEndpoint
      });

      const response: AxiosResponse<{
        access_token?: string;
        refresh_token?: string;
        token_type?: string;
        session_id?: string;
        user?: any;
        detail?: string;
      }> = await authAxios.post(authEndpoint, loginData);

      // 백엔드 응답 처리 - access_token이 있으면 성공
      if (!response.data.access_token) {
        throw new Error(response.data.detail || 'Owner login failed');
      }

      const { access_token, refresh_token, token_type, session_id, user: userData } =
        response.data;

      // 백엔드에서 user 정보를 포함한 경우
      let user: User;
      
      if (userData) {
        user = {
          id: String(userData.id),
          name: userData.name || userData.username || '독서실 오너',
          user_number: userData.email,
          phone: userData.phone || '',
          email: userData.email,
          is_active: userData.is_active !== undefined ? userData.is_active : true,
          role: userData.role || 'owner',
          permissions: userData.permissions || DEFAULT_OWNER_PERMISSIONS,
          created_at: userData.created_at || new Date().toISOString(),
          updated_at: userData.updated_at || new Date().toISOString(),
          ...(userData.organization_id && { organization_id: userData.organization_id }),
          ...(userData.organization_id && { org_id: userData.organization_id }),
          ...(userData.organization_name && { organization_name: userData.organization_name }),
          // 첫 로그인 관련 정보 추가
          ...(userData.first_login !== undefined && { first_login: userData.first_login }),
          ...(userData.temp_password !== undefined && { temp_password: userData.temp_password }),
        };
      } else {
        // 오너 사용자 정보 생성 (백엔드에서 user 정보가 없는 경우)
        user = {
          id: 'owner_1',
          name: '독서실 오너',
          user_number: loginData.username || 'unknown',
          phone: '010-9999-9999',
          email: loginData.username || 'unknown',
          is_active: true,
          role: 'owner',
          permissions: DEFAULT_OWNER_PERMISSIONS,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      }

      // refresh_token이 없으면 명확한 오류 발생
      if (!refresh_token) {
        console.error('❌ 백엔드에서 refresh_token을 제공하지 않았습니다!');
        throw new Error('로그인 응답에 refresh_token이 없습니다. 관리자에게 문의하세요.');
      }

      return {
        user,
        access_token,
        refresh_token,
        token_type: token_type || 'bearer',
        session_id,
      };
    } catch (error) {
      console.error('오너 로그인 API 오류:', error);
      throw error;
    }
  },

  // 로그아웃
  async logout(): Promise<void> {
    try {
      // 🔴 2026-09-01: 지금 이 브라우저가 쓰던 열쇠를 함께 보낸다.
      //   안 보내면 서버가 어느 기기인지 몰라 그 계정의 모든 기기를 로그아웃시킨다
      //   (원장이 로그아웃하면 매장의 다른 PC·직원까지 같이 튕기던 원인).
      const refreshToken =
        localStorage.getItem('auth_refresh_token') ||
        localStorage.getItem('refreshToken') ||
        undefined;
      await authAxios.post('/v1/auth/logout', { refresh_token: refreshToken });
    } catch (error) {
      console.error('로그아웃 API 오류:', error);
      // 로그아웃 오류는 throw하지 않고 로컬 정리만 진행
    }
  },

  // 토큰 갱신
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    try {

      const response: AxiosResponse<RefreshTokenResponse> =
        await authAxios.post(
          '/v1/auth/refresh',
          { refresh_token: refreshToken },  // 백엔드 필드명에 맞춤
          {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          }
        );

      return response.data;
    } catch (error) {
      console.error('❌ 토큰 갱신 API 오류:', error);
      throw error;
    }
  },

  // 현재 사용자 정보 조회
  async getCurrentUser(): Promise<User> {
    try {
      const response: AxiosResponse<any> =
        await authAxios.get('/v1/auth/me');


      // User 타입으로 변환 - member_number와 user_number 처리
      const user: User = {
        id: response.data.id,
        name: response.data.name,
        // user_number: member_number가 있으면 사용, 없으면 user_number, 그것도 없으면 username
        user_number: response.data.member_number || response.data.user_number || response.data.username,
        phone: response.data.phone || '', // API에 없는 필드
        email: response.data.email,
        is_active: response.data.is_active,
        created_at: response.data.created_at || new Date().toISOString(),
        updated_at: response.data.updated_at || new Date().toISOString(),
        // 추가 필드 (타입 안전성을 위해 조건부 할당)
        ...(response.data.role && { role: response.data.role }),
        ...(response.data.org_id && { org_id: response.data.org_id }),
        ...(response.data.member_number && { member_number: response.data.member_number }),
        ...(response.data.studyroom_name && { studyroom_name: response.data.studyroom_name }),
      };

      return user;
    } catch (error) {
      console.error('사용자 정보 조회 API 오류:', error);
      throw error;
    }
  },

  // 토큰 검증 (서버에 토큰 유효성 확인)
  async validateToken(): Promise<boolean> {
    try {
      // 🔧 자체 JWT Auth 사용
      await authAxios.get('/v1/auth/me');
      return true;
    } catch (error) {
      console.error('토큰 검증 실패:', error);
      return false;
    }
  },

  // 내 조직 정보 조회
  async getMyOrganization(): Promise<any> {
    try {
      const response = await authAxios.get('/auth/my-organization');
      return response.data;
    } catch (error) {
      console.error('조직 정보 조회 오류:', error);
      throw error;
    }
  },

  // 관리자 목록 조회 (권한이 있는 경우)
  async getAdmins(): Promise<any[]> {
    try {
      const response = await authAxios.get('/auth/admins');
      return response.data;
    } catch (error) {
      console.error('관리자 목록 조회 오류:', error);
      throw error;
    }
  },

  // 하위 직원 목록 조회 (오너 이상)
  async getSubordinates(): Promise<any[]> {
    try {
      const response = await authAxios.get('/auth/subordinates');
      return response.data;
    } catch (error) {
      console.error('하위 직원 목록 조회 오류:', error);
      throw error;
    }
  },

  // 비밀번호 변경
  async changePassword(currentPassword: string, newPassword: string): Promise<any> {
    try {
      // /api/v1/auth/change-password (baseURL=/api → /api/v1/auth/...)
      const response = await authAxios.post('/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      throw error;
    }
  },

  // 전화번호로 로그인
  async loginByPhone(credentials: PhoneLoginRequest): Promise<LoginResponse> {
    try {
      // 자체 JWT 인증: username 필드에 전화번호 전달
      const loginData = {
        username: credentials.phone,
        password: credentials.password
      };

      const response: AxiosResponse<{
        access_token?: string;
        refresh_token?: string;
        token_type?: string;
        session_id?: string;
        user?: any;
        detail?: string;
      }> = await authAxios.post('/v1/auth/login', loginData);

      // 백엔드 응답 처리 - access_token이 있으면 성공
      if (!response.data.access_token) {
        throw new Error(response.data.detail || 'Login failed');
      }

      const { access_token, refresh_token, token_type, session_id, user: userData } =
        response.data;

      // 백엔드에서 user 정보를 바로 반환하는 경우
      let user: User;
      
      if (userData) {
        // 백엔드가 user 정보를 포함한 경우
        user = {
          id: String(userData.id),
          name: userData.name || userData.username,
          // user_number: 우선순위 - member_number > user_number > email > phone
          user_number: userData.member_number || userData.user_number || userData.email || userData.phone,
          phone: userData.phone,
          email: userData.email,
          is_active: userData.is_active !== undefined ? userData.is_active : true,
          created_at: userData.created_at || new Date().toISOString(),
          updated_at: userData.updated_at || new Date().toISOString(),
          ...(userData.role && { role: userData.role }),
          ...(userData.business_id && { business_id: userData.business_id }),
          ...(userData.organization_id && { organization_id: userData.organization_id }),
          ...(userData.organization_id && { org_id: userData.organization_id }),
          ...(userData.DEFAULT_OWNER_PERMISSIONS && { DEFAULT_OWNER_PERMISSIONS: userData.DEFAULT_OWNER_PERMISSIONS }),
          // 첫 로그인 관련 정보 추가
          ...(userData.first_login !== undefined && { first_login: userData.first_login }),
          ...(userData.temp_password !== undefined && { temp_password: userData.temp_password }),
          // member_number도 별도로 저장 (디버깅용)
          ...(userData.member_number && { member_number: userData.member_number }),
        };
      } else {
        // 사용자 정보 가져오기 (토큰을 헤더에 직접 설정)
        const userResponse: AxiosResponse<UserXCircleResponse> = await authAxios.get(
          '/v1/auth/me',
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          }
        );

        // User 타입으로 변환
        user = {
          id: userResponse.data.id,
          name: userResponse.data.name,
          user_number: userResponse.data.username,
          phone: '', // API 응답에 없는 경우 빈 값
          email: userResponse.data.email,
          is_active: userResponse.data.is_active,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          // 추가 필드들
          ...(userResponse.data.role && { role: userResponse.data.role }),
          ...(userResponse.data.org_id && { org_id: userResponse.data.org_id }),
        };
      }

      // refresh_token이 없으면 명확한 오류 발생
      if (!refresh_token) {
        console.error('❌ 백엔드에서 refresh_token을 제공하지 않았습니다!');
        throw new Error('로그인 응답에 refresh_token이 없습니다. 관리자에게 문의하세요.');
      }

      return {
        user,
        access_token,
        refresh_token,
        token_type: token_type || 'bearer',
        session_id,
      };
    } catch (error: any) {
      console.error('전화번호 로그인 API 오류:', error);
      console.error('오류 상세:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method
        }
      });
      throw error;
    }
  },

  // 학생 회원번호로 로그인
  async studentLogin(data: { memberNumber: string }): Promise<{
    success: boolean;
    token: string;
    student: {
      id: string;
      name: string;
      memberNumber: string;
      email?: string;
      phone?: string;
    };
  }> {
    try {
      const response = await authAxios.post('/auth/student/login', data);
      return response.data;
    } catch (error) {
      console.error('학생 로그인 오류:', error);
      throw error;
    }
  },

  // 학생 전화번호로 로그인
  async studentLoginByPhone(data: { phone: string; password: string }): Promise<{
    success: boolean;
    token: string;
    student: {
      id: string;
      name: string;
      memberNumber: string;
      email?: string;
      phone?: string;
    };
  }> {
    try {
      const response = await authAxios.post('/auth/student/login-by-phone', data);
      return response.data;
    } catch (error) {
      console.error('학생 전화번호 로그인 오류:', error);
      throw error;
    }
  },
};

// 일반 API 요청용 Axios 인스턴스 (인증 토큰 자동 첨부)
export const apiClient = axios.create({
  baseURL: getAuthBaseURL(), // ⭐ nginx 프록시 대응
  timeout: 30000, // 30초로 증가
  headers: {
    'Content-Type': 'application/json',
  },
});

// 일반 API 요청 인터셉터
apiClient.interceptors.request.use(
  (config) => {
    // 🔧 auth_access_token 우선, accessToken은 호환성 유지
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 일반 API 응답 인터셉터 (토큰 만료시 자동 갱신)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // 🔧 auth_refresh_token 우선, refreshToken은 호환성 유지
      const refreshToken = localStorage.getItem('auth_refresh_token') || localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const refreshResponse = await authApi.refreshToken(refreshToken);
          const newAccessToken = refreshResponse.access_token;
          const newRefreshToken = refreshResponse.refresh_token || refreshToken;

          // Legacy 키에 저장
          localStorage.setItem('accessToken', newAccessToken);
          if (refreshResponse.refresh_token) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // DDD 키에도 저장 (TokenService 호환성 - 모든 3개 키 저장)
          try {
            const authToken = AuthToken.fromJWT(newAccessToken, newRefreshToken);
            AuthLocalStorageAdapter.saveToken(authToken);
            AuthLocalStorageAdapter.updateLastActivity();
          } catch (e) {
            console.warn('⚠️ DDD 토큰 저장 실패:', e);
          }

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          // [2025-11-22] 토큰 갱신 실패 시 자동 로그아웃 방지
          // 일시적인 네트워크 문제나 서버 이슈일 수 있으므로 조용히 실패 처리
          console.warn('🔄 토큰 갱신 실패 - 요청만 실패 처리 (로그아웃 안 함):', {
            currentPath: window.location.pathname,
            error: refreshError
          });

          // 요청만 실패 처리하고 사용자는 로그인 상태 유지
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default authApi;
